<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { StepKey } from '@/router/products'

/**
 * One decision-tree question screen, rendered generically from the step key.
 *
 * Every question has the same shape (a heading + a list of option tiles); the
 * per-step copy and layout come from STEP_UI, the option values from the parent
 * (which resolves product-specific sets, e.g. AURA's reduced optical/dental),
 * and the navigation stays in the parent via the `select` event.
 */

type Variant = 'child' | 'self'

type QuestionUi = {
  /** i18n key for the heading: a single key, or a {child,self} pair when it varies. */
  question: string | Record<Variant, string>
  /** i18n key for an option label, given the option value + the current variant. */
  optionLabelKey: (opt: string, variant: Variant) => string
  /** 3-up layout when the step offers three options. */
  triple?: boolean
  /** Whether option labels interpolate the age bounds (min/max). */
  ageParams?: boolean
}

/** Presentation metadata for each decision-tree question step (shared across
 *  products; the decision tree guarantees a step only appears where relevant). */
const STEP_UI: Partial<Record<StepKey, QuestionUi>> = {
  forWhom: {
    question: 'subscription.start.forWhom',
    optionLabelKey: (opt) => `subscription.start.forWhomOptions.${opt}`,
  },
  isStudent: {
    question: { child: 'subscription.start.studentChild', self: 'subscription.start.studentSelf' },
    optionLabelKey: (opt) => `subscription.start.yesNo.${opt}`,
  },
  age: {
    question: { child: 'subscription.start.ageQuestion.child', self: 'subscription.start.ageQuestion.self' },
    optionLabelKey: (opt, v) => `subscription.start.ageOptions.${v}.${opt}`,
    triple: true,
    ageParams: true,
  },
  internetProtection: {
    question: { child: 'subscription.start.internetQuestion.child', self: 'subscription.start.internetQuestion.self' },
    optionLabelKey: (opt) => `subscription.start.yesNo.${opt}`,
  },
  destination: {
    question: { child: 'subscription.start.destinationQuestion.child', self: 'subscription.start.destinationQuestion.self' },
    optionLabelKey: (opt) => `subscription.start.destinationOptions.${opt}`,
  },
  franceStay: {
    question: { child: 'subscription.start.franceStayQuestion.child', self: 'subscription.start.franceStayQuestion.self' },
    optionLabelKey: (opt) => `subscription.start.franceStayOptions.${opt}`,
    triple: true,
  },
  careFrequency: {
    question: { child: 'subscription.start.careQuestion.child', self: 'subscription.start.careQuestion.self' },
    optionLabelKey: (opt) => `subscription.start.careOptions.${opt}`,
    triple: true,
  },
  residency: {
    question: { child: 'subscription.start.residencyQuestion.child', self: 'subscription.start.residencyQuestion.self' },
    optionLabelKey: (opt) => `subscription.start.yesNo.${opt}`,
  },
  opticalExpenses: {
    question: { child: 'subscription.start.opticalQuestion.child', self: 'subscription.start.opticalQuestion.self' },
    optionLabelKey: (opt) => `subscription.start.opticalOptions.${opt}`,
    triple: true,
  },
  dentalNeeds: {
    question: { child: 'subscription.start.dentalQuestion.child', self: 'subscription.start.dentalQuestion.self' },
    optionLabelKey: (opt, v) => `subscription.start.dentalOptions.${v}.${opt}`,
    triple: true,
  },
  extraReimbursements: {
    question: { child: 'subscription.start.extraQuestion.child', self: 'subscription.start.extraQuestion.self' },
    optionLabelKey: (opt) => `subscription.start.yesNo.${opt}`,
  },
}

const props = defineProps<{
  stepKey: StepKey
  variant: Variant
  options: readonly string[]
  /** Age bounds interpolated into the age option labels. */
  ageLabelParams: { min: number; max: number }
}>()

defineEmits<{ select: [value: string] }>()

const { t } = useI18n()

const ui = computed(() => STEP_UI[props.stepKey])

const questionText = computed(() => {
  const q = ui.value?.question
  if (!q) return ''
  return typeof q === 'string' ? t(q) : t(q[props.variant])
})

function optionLabel(opt: string): string {
  const u = ui.value
  if (!u) return opt
  const key = u.optionLabelKey(opt, props.variant)
  return u.ageParams ? t(key, props.ageLabelParams) : t(key)
}
</script>

<template>
  <div class="journey-card__step">
    <h1 id="journey-question" class="journey-card__question">{{ questionText }}</h1>
    <div
      class="journey-card__choices"
      :class="{ 'journey-card__choices--triple': ui?.triple }"
      role="group"
    >
      <button
        v-for="opt in options"
        :key="opt"
        type="button"
        class="choice-tile"
        @click="$emit('select', opt)"
      >
        <span class="choice-tile__label">{{ optionLabel(opt) }}</span>
        <span class="choice-tile__radio" aria-hidden="true"></span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.journey-card__step {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.journey-card__question {
  font-size: var(--text-xl);
  line-height: var(--leading-tight);
}

.journey-card__choices {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
}
@media (min-width: 30rem) {
  .journey-card__choices { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 30rem) {
  .journey-card__choices--triple { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

/* Réponses de l'arbre : lignes radio (pastille + libellé). Le clic coche la
 * pastille ET avance aussitôt (navigation URL, gérée par le parent). */
.choice-tile {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.6rem;
  padding: 0.875rem 1.125rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  text-align: left;
  transition:
    border-color var(--dur-fast) var(--ease-out),
    background var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out);
}
.choice-tile:hover,
.choice-tile:focus-visible {
  border-color: var(--primary);
  background: color-mix(in oklab, var(--primary) 10%, white);
  color: var(--primary);
}
.choice-tile__label {
  flex: 1 1 auto;
  line-height: 1.3;
}
/* Pastille radio (cercle + point central qui se remplit au survol/focus). */
.choice-tile__radio {
  order: -1;
  flex: 0 0 auto;
  width: 1.15rem;
  height: 1.15rem;
  border: 2px solid var(--border-strong);
  border-radius: 50%;
  background: var(--color-white);
  display: grid;
  place-content: center;
  transition: border-color var(--dur-fast) var(--ease-out);
}
.choice-tile__radio::after {
  content: '';
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: var(--primary);
  transform: scale(0);
  transition: transform var(--dur-fast) var(--ease-out);
}
.choice-tile:hover .choice-tile__radio,
.choice-tile:focus-visible .choice-tile__radio {
  border-color: var(--primary);
}
.choice-tile:hover .choice-tile__radio::after,
.choice-tile:focus-visible .choice-tile__radio::after {
  transform: scale(1);
}
</style>
